/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { routes } from '@/route-paths';

/**
 * One entry of `digitalData.product[]` — legacy's minimal `productInfo` shape
 * (`productID`/`productName` only, no price/category/variant fields; see
 * `libOmnitureDataLayer.js`'s `addProduct`). Used on product, cart, and
 * checkout pages (checkout also separately needs `cart.item[]` — not yet
 * implemented, see `resolveStJudePageInfo`'s doc comment for what's deferred).
 */
export interface StJudeProductEntry {
    productInfo: { productID: string; productName: string };
}

/** `digitalData.search` — present on search-results pages only. */
export interface StJudeSearchData {
    searchTerm: string;
    resultsReturned: number;
    currentPaging: number;
}

/** `digitalData.cart` — present on the checkout page only (see `StJudeProductEntry`'s doc comment for why this duplicates `product[]` in a different shape). */
export interface StJudeCartData {
    item: StJudeProductEntry[];
}

/**
 * `digitalData.transaction` — present on the order-confirmation page only.
 * `taxTotal`/`shippingTotal` are strings (legacy formatted them as decimal
 * strings, e.g. `"8.25"`) even though the underlying order values are numbers.
 */
export interface StJudeTransactionData {
    transactionID: string;
    paymentType: string;
    taxTotal: string;
    shippingTotal: string;
    profile: { address: { state: string; zip: string } };
    item: StJudeProductEntry[];
}

export interface StJudeDigitalData {
    sysEnv: { siteCode: string; environment: string };
    page: { pageName: string; language: string; type: string };
    user: { userName: string };
    /** Present on product/cart/login/checkout/order-confirmation pages only — see `StJudeProductEntry`'s doc comment. */
    product?: StJudeProductEntry[];
    /** Present on search-results pages only. */
    search?: StJudeSearchData;
    /** Present on the checkout page only. */
    cart?: StJudeCartData;
    /** Present on the order-confirmation page only. */
    transaction?: StJudeTransactionData;
}

/** Builds a single `digitalData.product[]`/`cart.item[]` entry from a product id/name — see `StJudeProductEntry`'s doc comment. */
export function buildStJudeProductEntry(productId: string, productName: string): StJudeProductEntry {
    return { productInfo: { productID: productId, productName } };
}

/**
 * Line item shape minimal enough to match both a SCAPI Basket's
 * `productItems[]` and an Order's `productItems[]` structurally, without
 * importing either SCAPI type into this generic module.
 */
interface ProductEntrySource {
    productId?: string | null;
    productName?: string | null;
}

/**
 * Builds `digitalData.product[]`/`cart.item[]` entries from basket or order
 * line items — see `StJudeProductEntry`'s doc comment. Skips any line item
 * missing a `productId` (e.g. a not-yet-fully-loaded bonus/bundle line);
 * `productName` falls back to `''` rather than being skipped, matching
 * legacy's lack of a name fallback check.
 */
export function buildStJudeProductEntriesFromLineItems(items: ProductEntrySource[]): StJudeProductEntry[] {
    return items
        .filter((item): item is ProductEntrySource & { productId: string } => Boolean(item.productId))
        .map((item) => buildStJudeProductEntry(item.productId, item.productName ?? ''));
}

/**
 * The full `window.stjude` shape, matching legacy Sitegenesis's
 * `libOmnitureDataLayer.js` output: `digitalData` alongside a `$ready` queue
 * (kept as a static empty array here — this is shape parity for any
 * legacy-authored Adobe Launch rule that checks `Array.isArray(stjude.$ready)`
 * or pushes to it; this codebase doesn't implement queue-flush semantics).
 */
export interface StJudeGlobal {
    $ready: unknown[];
    digitalData: StJudeDigitalData;
}

export function wrapStJudeGlobal(digitalData: StJudeDigitalData): StJudeGlobal {
    return { $ready: [], digitalData };
}

/** Assigns `window.stjude`. Shared by every client-side dataLayer sync component so the cast lives in one place. */
export function assignStJudeGlobal(global: StJudeGlobal): void {
    if (typeof window === 'undefined') return;
    (window as typeof window & { stjude?: StJudeGlobal }).stjude = global;
}

/** Builds `/s/{siteId}/{segment}` — the legacy `basePath + pageName` convention every `stjude.digitalData.page.pageName` uses. */
export function buildStJudePagePath(siteId: string, segment: string): string {
    return `/s/${siteId}/${segment}`;
}

function lastPathSegment(pathname: string): string {
    const parts = pathname.split('/').filter(Boolean);
    return parts[parts.length - 1] ?? '';
}

function stripLeadingSlash(pathname: string): string {
    return pathname.replace(/^\//, '');
}

/**
 * Resolves `page.pageName`/`page.type` from a bare pathname (no
 * `/:siteId/:localeId` prefix — strip that with `getBarePath` from
 * `src/utils/seo.ts` before calling this) plus the resolved `siteId` (the
 * same `sysEnv.siteCode` value — see `buildStJudeDigitalData`).
 *
 * Category, product, and checkout pages need data this function doesn't have
 * access to (the category's full breadcrumb, the real in-flight checkout
 * step) — it returns single-segment/generic placeholders for those, which
 * are the value used for the very first server-rendered paint only.
 * Dedicated client components (all under `src/analytics/`) correct `page`
 * (category/product/checkout) and/or add the extra fields legacy populates
 * per page type (search/cart/login/checkout/order-confirmation — `product[]`,
 * `search`, `cart.item[]`, `transaction`): `StJudeCategoryDataLayerSync`,
 * `StJudeProductDataLayerSync`, `StJudeCheckoutDataLayerSync`,
 * `StJudeSearchDataLayerSync`, `StJudeBasketProductDataLayerSync` (cart and
 * login), and `StJudeOrderConfirmationDataLayerSync`. `StJudeDataLayerSync`
 * (src/analytics/stjude-datalayer-sync.tsx) knows to skip all of those
 * routes on every client-side navigation, so it never clobbers the more
 * specific/enriched value with this generic one.
 */
export function resolveStJudePageInfo(pathname: string, siteId: string): { pageName: string; pageType: string } {
    if (pathname === routes.home) {
        return { pageName: buildStJudePagePath(siteId, 'home'), pageType: 'content' };
    }
    if (pathname.startsWith('/search')) {
        return { pageName: buildStJudePagePath(siteId, 'search'), pageType: 'category' };
    }
    if (pathname.startsWith('/category')) {
        return { pageName: buildStJudePagePath(siteId, lastPathSegment(pathname)), pageType: 'category' };
    }
    if (pathname.startsWith('/product')) {
        return { pageName: buildStJudePagePath(siteId, lastPathSegment(pathname)), pageType: 'prodDetail' };
    }
    if (pathname === routes.cart) {
        return { pageName: buildStJudePagePath(siteId, 'cart'), pageType: 'cart' };
    }
    if (pathname === routes.login) {
        return { pageName: buildStJudePagePath(siteId, 'login'), pageType: 'login' };
    }
    if (pathname.startsWith('/checkout')) {
        return { pageName: buildStJudePagePath(siteId, 'checkout'), pageType: 'checkout' };
    }
    if (pathname.startsWith('/order-confirmation')) {
        return { pageName: buildStJudePagePath(siteId, 'confirmation'), pageType: 'confirmation' };
    }
    if (pathname.startsWith('/account')) {
        return { pageName: buildStJudePagePath(siteId, stripLeadingSlash(pathname)), pageType: 'account' };
    }
    // Content/CMS pages (e.g. /about-us) and anything else unrecognized — the
    // bare path segment IS the SFCC content ID for this route shape, so no
    // dedicated sync component is needed here (unlike category/product/checkout).
    return { pageName: buildStJudePagePath(siteId, stripLeadingSlash(pathname) || 'home'), pageType: 'content' };
}

/**
 * Category structure minimal enough to match `ShopperProducts.schemas['Category']`
 * structurally without importing SCAPI types into this generic module.
 */
interface CategoryBreadcrumbSource {
    id: string;
    parentCategoryTree?: { id?: string | null }[];
}

/**
 * Builds the `{categoryBreadcrumb}` segment from a SCAPI Category's
 * `parentCategoryTree` (root → current, `parentCategoryTree`'s last entry IS
 * the category itself — see `CategoryBreadcrumbs` component, which renders
 * every entry as a crumb including the current page). Falls back to the bare
 * category id when no tree is available (e.g. a root-level category).
 */
export function buildCategoryBreadcrumbSegment(category: CategoryBreadcrumbSource): string {
    const ids = (category.parentCategoryTree ?? [])
        .map((c) => c.id)
        .filter((id): id is string => Boolean(id) && id !== 'root');
    return ids.length > 0 ? ids.join('/') : category.id;
}

/**
 * Maps Storefront Next's checkout step enum (`CHECKOUT_STEPS` in
 * `src/components/checkout/utils/checkout-context-types.ts`) to the legacy
 * step-name vocabulary (`shipping`/`billing`/`summary`) Adobe Analytics
 * reports are already built against. There's no clean 1:1 mapping — legacy
 * had one "shipping" step where this app has four (contact info, pickup,
 * shipping address, shipping options) — so this is a best-effort grouping,
 * a product decision, not a technical derivation. Revisit with the
 * Analytics/reporting team if funnel data needs finer granularity.
 *
 * Takes a plain number (not the `CheckoutStep` type) to avoid this generic
 * tracking module depending on checkout-specific types.
 */
const CHECKOUT_STEP_LEGACY_NAMES: Record<number, string> = {
    0: 'shipping', // CONTACT_INFO
    1: 'shipping', // PICKUP
    2: 'shipping', // SHIPPING_ADDRESS
    3: 'shipping', // SHIPPING_OPTIONS
    4: 'billing', // PAYMENT
    5: 'summary', // PLACE_ORDER
};

export function mapCheckoutStepToLegacyStepName(step: number): string {
    return CHECKOUT_STEP_LEGACY_NAMES[step] ?? 'shipping';
}

export interface BuildStJudeDigitalDataInput {
    /**
     * `sysEnv.siteCode` — the `omnitureSiteCode` Business Manager custom
     * preference (Omniture/Adobe-specific identifier). NOT the same value as
     * `siteId` below — see that field's doc comment.
     */
    siteCode: string;
    /**
     * The `{siteId}` segment in every `page.pageName`'s `/s/{siteId}/...`
     * prefix — the real SFCC Business Manager Site ID (`site.alias ?? site.id`
     * in this codebase), matching legacy's `basePath = "/s/" +
     * dw.system.Site.getCurrent().getID()` in `libOmnitureDataLayer.js`. This
     * is deliberately a *different* value from `sysEnv.siteCode`
     * (`omnitureSiteCode`) above — legacy reads them from two unrelated
     * sources, so don't collapse these into one "site" parameter.
     */
    siteId: string;
    environment: string;
    pathname: string;
    language: string;
    /** Non-PII user identifier — encoded/hashed customer id, never raw email. `'anonymous'` when not authenticated. */
    userName: string | undefined;
}

/**
 * Builds the base `stjude.digitalData` object injected into `<head>` — the
 * Storefront Next equivalent of legacy's `var stjude = {...}` output in
 * `lib/datalayer.isml`. Only the always-present base shape (`sysEnv`, `page`,
 * `user`) is built here; see `resolveStJudePageInfo` doc comment for what's
 * deferred to dedicated per-route sync components.
 */
export function buildStJudeDigitalData({
    siteCode,
    siteId,
    environment,
    pathname,
    language,
    userName,
}: BuildStJudeDigitalDataInput): StJudeDigitalData {
    const { pageName, pageType } = resolveStJudePageInfo(pathname, siteId);

    return {
        sysEnv: { siteCode, environment },
        page: { pageName, language, type: pageType },
        user: { userName: userName || 'anonymous' },
    };
}
